import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Mail, Phone, MapPin } from "lucide-react";
import { LOGO } from "@/lib/images";

const STATUS = { new: "text-gold border-gold/40", contacted: "text-blue-300 border-blue-400/40", closed: "text-white/40 border-white/20" };

// Human-readable Eastern Time for the Passport List "Joined" column.
// Display-only — the stored timestamp and CSV export stay ISO-8601 UTC.
const fmtJoined = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  const date = d.toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true });
  return `${date} · ${time} ET`;
};

export default function Admin() {
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");
  const [subscribers, setSubscribers] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [activeBooking, setActiveBooking] = useState(null);
  const [subMenuId, setSubMenuId] = useState(null);
  const [subConfirm, setSubConfirm] = useState(null);

  const load = () => {
    api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/quotes/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
    api.get("/booking-requests").then((r) => setBookingRequests(r.data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (user === null) return <div className="min-h-screen bg-noir flex items-center justify-center text-white/40">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  const updateStatus = async (id, status) => {
    try { await api.patch(`/quotes/${id}`, { status }); toast.success("Status updated"); load(); setActive((a) => a && a.id === id ? { ...a, status } : a); }
    catch { toast.error("Update failed"); }
  };

  const updateBookingStatus = async (id, status) => {
    try { await api.patch(`/booking-requests/${id}`, { status }); toast.success("Status updated"); load(); setActiveBooking((a) => a && a.id === id ? { ...a, status } : a); }
    catch { toast.error("Update failed"); }
  };

  const downloadSubscriberCsv = async () => {
    try {
      const r = await api.get("/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "inforno-passport-list.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV export failed. Please try again."); }
  };

  // Phase 1 subscriber management: Remove from Passport List / Block as Spam.
  // On failure the row is deliberately kept so the UI never implies success.
  const removeSubscriber = async (s) => {
    try {
      await api.delete(`/subscribers/${s.id}`);
      setSubscribers((subs) => subs.filter((x) => x.id !== s.id));
      toast.success("Subscriber removed from the Passport List.");
    } catch { toast.error("Remove failed — the subscriber record was kept. Please try again."); }
    setSubConfirm(null);
  };

  const blockSubscriber = async (s) => {
    try {
      await api.post(`/subscribers/${s.id}/block-spam`);
      setSubscribers((subs) => subs.filter((x) => x.id !== s.id));
      toast.success("Subscriber blocked as spam and removed.");
    } catch { toast.error("Block failed — the subscriber record was kept. Please try again."); }
    setSubConfirm(null);
  };

  const shown = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div className="min-h-screen bg-noir text-ink">
      <header className="border-b border-white/10 px-6 md:px-10 h-20 flex items-center justify-between sticky top-0 bg-noir/90 backdrop-blur-xl z-40">
        <div>
          <Link to="/"><img src={LOGO} alt="Inforno Travel" className="h-9 w-auto" /></Link>
          <span className="ml-4 font-body text-[10px] uppercase tracking-[0.3em] text-gold">Quote Console</span>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 text-white/60 hover:text-gold transition-colors text-xs uppercase tracking-[0.2em]">
          <LogOut size={15} /> Logout
        </button>
      </header>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        <h1 className="font-serif text-4xl md:text-5xl font-light mb-10">Quote Requests</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          {[["Total", stats.total], ["New", stats.new], ["Contacted", stats.contacted], ["Closed", stats.closed]].map(([k, v]) => (
            <div key={k} data-testid={`stat-${k.toLowerCase()}`} className="bg-noir p-6">
              <p className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40">{k}</p>
              <p className="font-serif text-4xl font-light mt-2">{v}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-6 mb-6 border-b border-white/10">
          {["all", "new", "contacted", "closed"].map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`pb-3 font-body text-xs uppercase tracking-[0.2em] transition-colors ${filter === f ? "text-gold border-b-2 border-gold -mb-px" : "text-white/40 hover:text-white/70"}`}>
              {f}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <p className="text-white/40 py-20 text-center font-light">No requests yet.</p>
        ) : (
          <div className="border border-white/10">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-body text-[10px] uppercase tracking-[0.2em] text-white/40">
              <div className="col-span-3">Name</div><div className="col-span-3">Travel Type</div><div className="col-span-2">Destination</div><div className="col-span-2">Received</div><div className="col-span-2">Status</div>
            </div>
            {shown.map((q) => (
              <button key={q.id} data-testid={`quote-row-${q.id}`} onClick={() => setActive(q)}
                className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-b border-white/10 hover:bg-white/[0.03] transition-colors">
                <div className="col-span-3">
                  <p className="font-body text-sm">{`${q.first_name} ${q.last_name}`.trim()}
                    {q.is_group_trip === "yes" && <span data-testid={`quote-group-badge-${q.id}`} className="inline-block ml-2 border border-gold/60 text-gold px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] align-middle">Group</span>}
                  </p>
                  <p className="font-body text-xs text-white/40">{q.email}</p>
                </div>
                <div className="col-span-3 font-body text-sm text-white/70 self-center">{q.travel_type}</div>
                <div className="col-span-2 font-body text-sm text-white/50 self-center">{q.destination || "—"}</div>
                <div className="col-span-2 font-body text-xs text-white/40 self-center">{new Date(q.created_at).toLocaleDateString()}</div>
                <div className="col-span-2 self-center">
                  <span className={`inline-block border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${STATUS[q.status] || STATUS.new}`}>{q.status}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="mt-16">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-3xl md:text-4xl font-light">Booking Requests</h2>
            <span data-testid="booking-request-count" className="font-body text-[11px] uppercase tracking-[0.2em] text-gold">{bookingRequests.length} requests</span>
          </div>
          {bookingRequests.length === 0 ? (
            <p className="text-white/40 py-10 text-center font-light border border-white/10">No booking requests yet.</p>
          ) : (
            <div className="border border-white/10">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-body text-[10px] uppercase tracking-[0.2em] text-white/40">
                <div className="col-span-3">Name</div><div className="col-span-3">Trip / Supplier</div><div className="col-span-2">Destination</div><div className="col-span-2">Received</div><div className="col-span-2">Status</div>
              </div>
              {bookingRequests.map((b) => (
                <button key={b.id} data-testid={`booking-row-${b.id}`} onClick={() => setActiveBooking(b)}
                  className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-b border-white/10 hover:bg-white/[0.03] transition-colors">
                  <div className="col-span-3">
                    <p className="font-body text-sm">{`${b.first_name} ${b.last_name}`.trim()}</p>
                    <p className="font-body text-xs text-white/40">{b.email}</p>
                    {b.request_ref && <p className="font-body text-[10px] tracking-[0.15em] text-gold/80 mt-1">{b.request_ref}</p>}
                  </div>
                  <div className="col-span-3 font-body text-sm text-white/70 self-center">{b.supplier_name || b.travel_type || "—"}</div>
                  <div className="col-span-2 font-body text-sm text-white/50 self-center">{b.destination || "—"}</div>
                  <div className="col-span-2 font-body text-xs text-white/40 self-center">{new Date(b.created_at).toLocaleDateString()}</div>
                  <div className="col-span-2 self-center">
                    <span className={`inline-block border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${STATUS[b.status] || STATUS.new}`}>{b.status}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-16">
          <div className="flex items-baseline justify-between mb-6 flex-wrap gap-4">
            <h2 className="font-serif text-3xl md:text-4xl font-light">Passport List</h2>
            <div className="flex items-center gap-5">
              <span data-testid="subscriber-count" className="font-body text-[11px] uppercase tracking-[0.2em] text-gold">{subscribers.length} subscribers</span>
              <button data-testid="download-subscribers-csv" onClick={downloadSubscriberCsv} disabled={subscribers.length === 0}
                className="border border-gold text-gold px-5 py-2.5 font-body text-[11px] uppercase tracking-[0.2em] hover:bg-gold hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                Download CSV
              </button>
            </div>
          </div>
          {subscribers.length === 0 ? (
            <p className="text-white/40 py-10 text-center font-light border border-white/10">No subscribers yet.</p>
          ) : (
            <div className="border border-white/10">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-body text-[10px] uppercase tracking-[0.2em] text-white/40">
                <div className="col-span-3">Name</div><div className="col-span-3">Email</div><div className="col-span-2">Source</div><div className="col-span-2">Joined</div><div className="col-span-1">Status</div><div className="col-span-1 text-right">Actions</div>
              </div>
              {subscribers.map((s) => (
                <div key={s.id} data-testid={`subscriber-row-${s.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-4 px-6 py-4 border-b border-white/10">
                  <div className="col-span-3 font-body text-sm">{s.first_name || "—"}</div>
                  <div className="col-span-3 font-body text-sm text-white/60">{s.email}</div>
                  <div className="col-span-2 font-body text-xs text-white/40 self-center">{[s.source_page, s.campaign_source].filter(Boolean).join(" · ") || "—"}</div>
                  <div className="col-span-2 font-body text-xs text-white/40 self-center" data-testid={`subscriber-joined-${s.id}`}>{fmtJoined(s.subscription_timestamp)}</div>
                  <div className="col-span-1 self-center">
                    <span data-testid={`subscriber-status-${s.id}`} className={`inline-block border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${s.subscriber_status === "active" ? "text-gold border-gold/40" : "text-white/40 border-white/20"}`}>{s.subscriber_status || "—"}</span>
                  </div>
                  <div className="col-span-1 self-center md:text-right relative">
                    {s.subscriber_status === "active" && (
                      <>
                        <button data-testid={`subscriber-actions-${s.id}`} aria-label={`Actions for ${s.email}`}
                          onClick={() => setSubMenuId(subMenuId === s.id ? null : s.id)}
                          className="text-white/40 hover:text-gold transition-colors px-2 py-1 font-body text-sm tracking-[0.2em]">•••</button>
                        {subMenuId === s.id && (
                          <>
                            <div className="fixed inset-0 z-30" onClick={() => setSubMenuId(null)} />
                            <div data-testid={`subscriber-menu-${s.id}`} className="absolute right-0 top-full mt-1 z-40 min-w-[220px] border border-white/10 bg-[#0a0a0a] shadow-2xl">
                              <button data-testid={`subscriber-remove-${s.id}`} onClick={() => { setSubMenuId(null); setSubConfirm({ type: "remove", sub: s }); }}
                                className="block w-full px-4 py-3 font-body text-xs text-white/70 hover:text-gold hover:bg-white/5 transition-colors text-left">Remove from Passport List</button>
                              <button data-testid={`subscriber-block-${s.id}`} onClick={() => { setSubMenuId(null); setSubConfirm({ type: "block", sub: s }); }}
                                className="block w-full px-4 py-3 font-body text-xs text-red-300/90 hover:bg-red-400/10 transition-colors text-left border-t border-white/10">Block as Spam</button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {active && (
        <div data-testid="quote-detail-modal" className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={() => setActive(null)}>
          <div className="bg-[#0a0a0a] border border-white/10 max-w-2xl w-full max-h-[85vh] overflow-y-auto p-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-8">
              <div>
                <p className="font-body text-[10px] uppercase tracking-[0.3em] text-gold mb-2">{active.travel_type}</p>
                <h2 className="font-serif text-3xl font-light">{`${active.first_name} ${active.last_name}`.trim()}</h2>
              </div>
              <button data-testid="modal-close" onClick={() => setActive(null)} className="text-white/40 hover:text-gold text-2xl leading-none">×</button>
            </div>
            <div className="space-y-4 font-body text-sm">
              <a href={`mailto:${active.email}`} className="flex items-center gap-3 text-white/70 hover:text-gold"><Mail size={15} className="text-gold" />{active.email}</a>
              {active.phone && <div className="flex items-center gap-3 text-white/70"><Phone size={15} className="text-gold" />{active.phone}</div>}
              {active.destination && <div className="flex items-center gap-3 text-white/70"><MapPin size={15} className="text-gold" />{active.destination}</div>}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Travelers</p><p>{active.travelers || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Travel Dates</p><p>{active.travel_dates || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Group Trip</p><p data-testid="quote-modal-group">{active.is_group_trip === "yes" ? <span className="inline-block border border-gold/60 text-gold px-2 py-0.5 text-[10px] uppercase tracking-[0.2em]">Group{active.travelers ? ` · ~${active.travelers}` : ""}</span> : (active.is_group_trip === "no" ? "No" : "—")}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Referred By</p><p data-testid="quote-modal-referred-by">{active.referred_by || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Rooms</p><p>{active.rooms || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Departure City</p><p>{active.departure_city || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Purpose</p><p>{active.purpose || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Preferred Contact</p><p>{active.contact_method || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Received</p><p>{new Date(active.created_at).toLocaleString()}</p></div>
              </div>
              {active.details && <div className="pt-4 border-t border-white/10"><p className="text-white/40 text-xs uppercase tracking-wider mb-2">Additional Details</p><p className="text-white/80 leading-relaxed font-light">{active.details}</p></div>}
            </div>
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Update Status</p>
              <div className="flex gap-3">
                {["new", "contacted", "closed"].map((s) => (
                  <button key={s} data-testid={`set-status-${s}`} onClick={() => updateStatus(active.id, s)}
                    className={`border px-5 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors ${active.status === s ? "bg-gold text-black border-gold" : "border-white/20 text-white/60 hover:border-gold hover:text-gold"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeBooking && (
        <div data-testid="booking-detail-modal" className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={() => setActiveBooking(null)}>
          <div className="bg-[#0a0a0a] border border-white/10 max-w-2xl w-full max-h-[85vh] overflow-y-auto p-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-8">
              <div>
                <p className="font-body text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Reservation Request{activeBooking.request_ref ? ` · ${activeBooking.request_ref}` : ""}{activeBooking.travel_type ? ` · ${activeBooking.travel_type}` : ""}</p>
                <h2 className="font-serif text-3xl font-light">{`${activeBooking.first_name} ${activeBooking.last_name}`.trim()}</h2>
              </div>
              <button data-testid="booking-modal-close" onClick={() => setActiveBooking(null)} className="text-white/40 hover:text-gold text-2xl leading-none">×</button>
            </div>
            <div className="space-y-4 font-body text-sm">
              <a href={`mailto:${activeBooking.email}`} className="flex items-center gap-3 text-white/70 hover:text-gold"><Mail size={15} className="text-gold" />{activeBooking.email}</a>
              {activeBooking.phone && <div className="flex items-center gap-3 text-white/70"><Phone size={15} className="text-gold" />{activeBooking.phone}</div>}
              {activeBooking.destination && <div className="flex items-center gap-3 text-white/70"><MapPin size={15} className="text-gold" />{activeBooking.destination}</div>}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Supplier / Trip Name</p><p>{activeBooking.supplier_name || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Room / Cabin / Option</p><p>{activeBooking.selected_option || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Travel Dates</p><p>{activeBooking.travel_dates || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Departure Date</p><p data-testid="booking-modal-departure">{activeBooking.departure_date ? new Date(`${activeBooking.departure_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Return Date</p><p data-testid="booking-modal-return">{activeBooking.return_date ? new Date(`${activeBooking.return_date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Travelers</p><p>{activeBooking.travelers || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Departure City</p><p>{activeBooking.departure_city || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Reference #</p><p>{activeBooking.reference_number || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Preferred Contact</p><p>{activeBooking.contact_method || "—"}</p></div>
                <div><p className="text-white/40 text-xs uppercase tracking-wider mb-1">Received</p><p>{new Date(activeBooking.created_at).toLocaleString()}</p></div>
              </div>
              {activeBooking.notes && <div className="pt-4 border-t border-white/10"><p className="text-white/40 text-xs uppercase tracking-wider mb-2">Notes / Special Requests</p><p className="text-white/80 leading-relaxed font-light">{activeBooking.notes}</p></div>}
            </div>
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Update Status</p>
              <div className="flex gap-3">
                {["new", "contacted", "closed"].map((s) => (
                  <button key={s} data-testid={`booking-set-status-${s}`} onClick={() => updateBookingStatus(activeBooking.id, s)}
                    className={`border px-5 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors ${activeBooking.status === s ? "bg-gold text-black border-gold" : "border-white/20 text-white/60 hover:border-gold hover:text-gold"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {subConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          data-testid={subConfirm.type === "block" ? "block-confirm-dialog" : "remove-confirm-dialog"}>
          <div className="border border-white/10 bg-[#0a0a0a] p-8 max-w-md w-full">
            <h3 className="font-serif text-2xl font-light mb-4">{subConfirm.type === "block" ? "Block this subscriber as spam?" : "Remove subscriber?"}</h3>
            <p className="font-body text-sm text-white/60 font-light leading-relaxed">
              <span className="text-gold">{subConfirm.sub.email}</span>{" "}
              {subConfirm.type === "block"
                ? "will be removed from the Passport List and prevented from being added again through the public Passport List signup form."
                : "will be removed from the active Passport List."}
            </p>
            {subConfirm.type === "remove" && (
              <p className="font-body text-xs text-white/40 font-light leading-relaxed mt-3">
                This action only removes the Passport List subscriber record. It does not represent a formal unsubscribe from every Inforno Travel system.
              </p>
            )}
            <div className="flex gap-3 justify-end mt-8">
              <button data-testid="subscriber-action-cancel" onClick={() => setSubConfirm(null)}
                className="border border-white/20 px-5 py-2.5 font-body text-[11px] uppercase tracking-[0.2em] text-white/60 hover:border-white/50 transition-colors">Cancel</button>
              <button data-testid={subConfirm.type === "block" ? "confirm-block-btn" : "confirm-remove-btn"}
                onClick={() => (subConfirm.type === "block" ? blockSubscriber(subConfirm.sub) : removeSubscriber(subConfirm.sub))}
                className={subConfirm.type === "block"
                  ? "border border-red-400/60 text-red-300 px-5 py-2.5 font-body text-[11px] uppercase tracking-[0.2em] hover:bg-red-400 hover:text-black transition-colors"
                  : "border border-gold text-gold px-5 py-2.5 font-body text-[11px] uppercase tracking-[0.2em] hover:bg-gold hover:text-black transition-colors"}>
                {subConfirm.type === "block" ? "Block & Remove" : "Remove Subscriber"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
